import { useIntl } from 'react-intl'

import Group from 'components/utils/Group'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import type { BookedItemPrice } from 'tripPlanner/utils/bookedOfferPricing'

interface Props {
  /** Undefined until the order resolves, or when it holds no item for this offer. */
  price: BookedItemPrice | undefined
  isLoading: boolean
}

/**
 * The amount paid for a booked item, shown wherever a trip item exposes its pricing.
 *
 * The order is fetched separately from the surrounding content, so the price lands after
 * first paint — the skeleton mirrors the caption and price lines to stop the row reflowing.
 */
function BookedPriceDetails({ price, isLoading }: Props) {
  const intl = useIntl()

  if (isLoading) {
    return (
      <Group direction="vertical" gap={4}>
        <LoadingBox height={16} width={64} borderRadius="S" />
        <LoadingBox
          height={28}
          width={140}
          borderRadius="S"
          data-testid="booked-price-skeleton"
        />
      </Group>
    )
  }

  if (!price) return null

  // Experiences carry no stay length, so they always fall through to the flat caption.
  const priceLabel = price.nights > 0 ?
      intl.formatMessage(
        {
          id: 'VptEfUz3TGyXS80JpE6I',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Caption above the amount paid for a booked hotel stay in the trip planner chat hotel drawer, showing how many nights the booking covers',
        },
        { count: price.nights },
      ) :
      intl.formatMessage({
        id: 'X8a3bT2nXXD5jp7jJJjp',
        defaultMessage: 'Total paid',
        description: 'Caption above the amount paid for a booked hotel stay in the trip planner chat hotel drawer, used when the stay length is unknown',
      })

  return (
    <PriceDetails
      price={price.total}
      currencyCode={price.currency}
      priceLabel={priceLabel}
      totalLabel="total"
      showTaxesAndFees={false}
    />
  )
}

export default BookedPriceDetails
